import React, { useEffect } from 'react'
import { useSession } from './SessionProvider'
import { useBalanceStore } from './UserBalance'
import { getTokenByDenom } from '@/services/token-service'
import socketIO from '@/services/socket'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { shouldApplyBalanceUpdate } from './balanceUpdate'

const AccountContext = React.createContext<any>({} as any)

const accountSubscribe = () => {
  const currentAccount = useSession.getState().account

  socketIO.emit(
    'account:subscribe',
    currentAccount && {
      player: currentAccount.injectiveAddress,
      session: currentAccount.privateKey.toBech32(),
    }
  )
}

export const AccountProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { account } = useSession()

  useEffect(() => {
    const onBalance = (data: any) => {
      try {
        const height = data.height

        let result = Object.fromEntries(
          Object.entries(data.coins)
            .map(([key, value]: any) => {
              const metadata = getTokenByDenom(key)

              if (!metadata) {
                return null
              }

              const currentBalance = useBalanceStore.getState().balance[key]
              const gameAccountUpdated = value.gameAccountUpdated ?? data.gameAccountUpdated ?? 0
              const shouldUpdate = shouldApplyBalanceUpdate(
                height,
                currentBalance?.height,
                gameAccountUpdated,
                currentBalance?.gameAccountUpdated
              )

              if (!shouldUpdate) {
                return null
              }

              return [
                key,
                {
                  ...currentBalance,
                  height,
                  gameAccountUpdated,
                  amount: value.amount != null ? new TokenMoney(value.amount, key) : currentBalance.amount,
                  bank: value.bank != null ? new TokenMoney(value.bank, key) : currentBalance.bank,
                  bonus: value.bonus != null ? new TokenMoney(value.bonus, key) : currentBalance.bonus,
                  game_balance:
                    value.game_balance != null ? new TokenMoney(value.game_balance, key) : currentBalance.game_balance,
                  game_main_balance:
                    value.game_main_balance != null
                      ? new TokenMoney(value.game_main_balance, key)
                      : currentBalance.game_main_balance,
                  game_bonus_balance:
                    value.game_bonus_balance != null
                      ? new TokenMoney(value.game_bonus_balance, key)
                      : currentBalance.game_bonus_balance,
                  game_main_contract_balance:
                    value.game_main_contract_balance != null
                      ? new TokenMoney(value.game_main_contract_balance, key)
                      : currentBalance.game_main_contract_balance,
                  game_bonus_contract_balance:
                    value.game_bonus_contract_balance != null
                      ? new TokenMoney(value.game_bonus_contract_balance, key)
                      : currentBalance.game_bonus_contract_balance,
                },
              ]
            })
            .filter((x) => x !== null)
        )
        useBalanceStore.getState().updateBalance(result, true)
        useBalanceStore.setState((x) => ({
          raw: {
            ...x.raw,
            ...data.coins,
          },
        }))
      } catch (e) {
        console.log(e)
      }
    }

    const onConnect = () => {
      accountSubscribe()
    }

    const onTx = (data: any) => {
      window.dispatchEvent(new CustomEvent('transaction', { detail: data }))
    }

    socketIO.on('tx', onTx)
    socketIO.on('balance', onBalance)
    socketIO.on('connect', onConnect)

    return () => {
      socketIO.off('tx', onTx)
      socketIO.off('balance', onBalance)
      socketIO.off('connect', onConnect)
    }
  }, [])

  useEffect(() => {
    accountSubscribe()
  }, [account])

  return <AccountContext.Provider value={{}}>{children}</AccountContext.Provider>
}
